import { Component, ViewEncapsulation, ViewChild, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import {
    CreateUserAccountBtnClicked,
    ResetCreateProfileState,
} from '../../state/actions/create-profile-ui-actions';
import { MatStepper } from '@angular/material/stepper';
import {
    CreateBusinessAccountFormData,
    CreateUserAccountFormData,
    CreateUserAccountPacket,
    TypeEnum,
} from '../../model/form-data';
import { AppState } from 'src/app/viewbid/state/app-state';
import { CreateProfileState } from '../../state/create-profile-state';
import { selectCreateProfileState } from '../../state/create-profile-selector';
import { createProfileInitialState } from '../../state/create-profile-reducers';
import { AccountCreationFailedActionProps } from '../../state/actions/create-profile-service-actions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CreateBusinessProfileComponent } from '../../components/create-business-profile/create-business-profile.component';
import { CreateUserProfileComponent } from '../../components/create-user-profile/create-user-profile.component';
import { CreateCharityProfileComponent } from '../../components/create-charity-profile/create-charity-profile.component';
import { CreateNotificationPreferencesComponent } from '../../components/create-notification-preferences/create-notification-preferences.component';
import { prependCanadaAndUSCountryCode } from '../../../../utils/prepend-country-code';
import { NotificationPreferencesFormData } from '../../model/form-data';
import { CreateProfileComponentService } from '../../services/create.profile.component.service';
import { ProfileService } from 'src/app/viewbid/shared/services/profile.service';

enum StepperSections {
    Profile = 0,
    Business = 1,
    Charity = 2,
    NotificationPreferences = 3,
}

@Component({
    selector: 'create-profile',
    templateUrl: './create-profile-home-page.component.html',
    styleUrls: ['./create-profile-home-page.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class CreateProfileHomePageComponent implements OnInit {
    isLoading: boolean = true;
    createProfileState: CreateProfileState = createProfileInitialState;

    createUserProfileFormData: CreateUserAccountFormData | null = null;
    createBusinessProfileFormData: CreateBusinessAccountFormData | null = null;
    createCharityProfileFormData: CreateBusinessAccountFormData | null = null;
    notificationPreferenceFormData: NotificationPreferencesFormData | null =
        null;

    isProfileSectionCompleted = false;
    isBusinessPreferencesSectionCompleted = false;
    isCharityPreferencesSectionCompleted = false;
    isNotificationPreferencesSectionCompleted = false;
    isWaitingForAPIResponse = false;

    businessSelected: boolean = false;
    charitySelected: boolean = false;

    @ViewChild('stepper') private stepper!: MatStepper;
    @ViewChild('userProfileSection')
    private userProfileSection!: CreateUserProfileComponent;
    @ViewChild('businessProfileSection')
    private businessProfileSection!: CreateBusinessProfileComponent;
    @ViewChild('charityProfileSection')
    private charityProfileSection!: CreateCharityProfileComponent;
    @ViewChild('notificationPreferencesSection')
    private notificationPreferencesSection!: CreateNotificationPreferencesComponent;

    constructor(
        public translate: TranslateService,
        private store: Store<AppState>,
        private noti: MatSnackBar,
        private router: Router,
        private profileService: ProfileService,
        private createProfileComponentService: CreateProfileComponentService,
    ) {
        this.profileService.subscribeToUserAdded();
        this.isLoading = false;
        store
            .select(selectCreateProfileState)
            .subscribe((state: CreateProfileState) => {
                this.createProfileState = state;

                if (state.result instanceof AccountCreationFailedActionProps) {
                    //reset state here after displaying toast
                    const err: AccountCreationFailedActionProps =
                        state.result as AccountCreationFailedActionProps;
                    this.noti.open(
                        'Account Creation Failed: ' + err.message,
                        undefined,
                        {
                            duration: 3000,
                            verticalPosition: 'top',
                        },
                    );
                    this.store.dispatch(new ResetCreateProfileState());
                } else if (state.result != null) {
                    this.store.dispatch(new ResetCreateProfileState());
                    this.router.navigate([
                        'profiles/registration-confirmation',
                    ]);
                }
            });
    }

    ngOnInit(): void {
        this.createProfileComponentService
            .getFormValidity()
            .subscribe((isValid: boolean) => {
                if (isValid) {
                    this.isProfileSectionCompleted = true;
                } else {
                    this.isProfileSectionCompleted = false;
                }
            });
    }

    stepperGoBack(): void {
        this.stepper.previous();
    }

    stepperGoForward(): void {
        this.stepper.next();
    }

    setCreateUserProfileFormData(event: CreateUserAccountFormData): void {
        this.createUserProfileFormData = event;
        this.stepperGoForward();
    }

    selectBusiness(ev: any): void {
        this.charitySelected = false;
        this.businessSelected = (ev.target as HTMLInputElement).checked;
    }

    selectCharity(ev: any): void {
        this.businessSelected = false;
        this.charitySelected = (ev.target as HTMLInputElement).checked;
    }

    setCreateBusinessProfileFormData(
        event: CreateBusinessAccountFormData,
    ): void {
        this.isBusinessPreferencesSectionCompleted = true;
        this.createBusinessProfileFormData = event;
        this.stepperGoForward();
    }

    setCreateCharityProfileFormData(
        event: CreateBusinessAccountFormData,
    ): void {
        this.isCharityPreferencesSectionCompleted = true;
        this.createCharityProfileFormData = event;
        this.stepperGoForward();
    }

    setNotificationPreferences(event: NotificationPreferencesFormData): void {
        // TODO: do something with the preference
        // eslint-disable-next-line
        const pref: NotificationPreferencesFormData = event;

        this.notificationPreferenceFormData = pref;
        this.isNotificationPreferencesSectionCompleted = true;
        this.submitAllSections();
    }

    validateAllSections(): boolean {
        if (!this.userProfileSection.validate()) {
            this.stepper.selectedIndex = StepperSections.Profile;
            this.isProfileSectionCompleted = false;
            return false;
        }

        if (this.businessSelected && !this.businessProfileSection.validate()) {
            this.stepper.selectedIndex = StepperSections.Business;
            this.isBusinessPreferencesSectionCompleted = false;
            return false;
        }

        if (this.charitySelected && !this.charityProfileSection.validate()) {
            this.stepper.selectedIndex = StepperSections.Charity;
            this.isCharityPreferencesSectionCompleted = false;
            return false;
        }

        if (!this.notificationPreferencesSection.validate()) {
            this.stepper.selectedIndex =
                StepperSections.NotificationPreferences;
            this.isNotificationPreferencesSectionCompleted = false;
            return false;
        }

        return true;
    }

    submitAllSections(): void {
        if (!this.validateAllSections()) return;
        if (
            this.createUserProfileFormData != null &&
            this.notificationPreferenceFormData != null
        ) {
            this.createUserProfileFormData.phoneNumber =
                prependCanadaAndUSCountryCode(
                    this.createUserProfileFormData.phoneNumber,
                );

            let combinedFormData: CreateUserAccountPacket = {
                ...this.createUserProfileFormData,
                ...this.notificationPreferenceFormData,
            };

            if (
                this.businessSelected &&
                this.createBusinessProfileFormData != null
            ) {
                this.createBusinessProfileFormData.phoneNumber =
                    prependCanadaAndUSCountryCode(
                        this.createBusinessProfileFormData.phoneNumber,
                    );

                combinedFormData = {
                    ...combinedFormData,
                    organization: {
                        type: TypeEnum.BUSINESS,
                        ...this.createBusinessProfileFormData,
                    },
                };
            }

            if (
                this.charitySelected &&
                this.createCharityProfileFormData != null
            ) {
                this.createCharityProfileFormData.phoneNumber =
                    prependCanadaAndUSCountryCode(
                        this.createCharityProfileFormData.phoneNumber,
                    );

                combinedFormData = {
                    ...combinedFormData,
                    organization: {
                        type: TypeEnum.CHARITY,
                        ...this.createCharityProfileFormData,
                    },
                };
            }
            this.store.dispatch(
                new CreateUserAccountBtnClicked(combinedFormData),
            );
        }
    }
}
